import {
  CSSProperties,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  columnOrderingFeature,
  columnResizingFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  ColumnVisibilityState,
  createColumnHelper,
  createExpandedRowModel,
  createPaginatedRowModel,
  createSortedRowModel,
  ExpandedState,
  rowExpandingFeature,
  rowPaginationFeature,
  rowSortingFeature,
  sortFn_alphanumeric,
  sortFn_basic,
  sortFn_datetime,
  sortFn_text,
  SortingState,
  tableFeatures,
  useTable,
} from '@tanstack/react-table'
import {
  closestCenter,
  DndContext,
} from '@dnd-kit/core'
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers'
import {
  horizontalListSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable'
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  Group,
  MultiSelect,
  Pagination,
  Paper,
  Popover,
  ScrollArea,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  Tooltip,
} from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import { format } from 'date-fns'
import {
  Calendar,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Download,
  RefreshCw,
  SlidersHorizontal,
} from 'lucide-react'
import { CSVLink } from 'react-csv'
import { useSearchParams } from 'react-router-dom'
import {
  FaBan,
  FaCheckCircle,
} from 'react-icons/fa'
import moment from 'moment-timezone'
import {
  countInstalledValves,
  formatPercentage,
  getDeviceStatus,
  getPowerStatus,
  POWER_STATUS_VALUES,
} from '@common/utils/helperFunctions'
import { hasPermissions } from '@common/utils/validation/authValidators'
import { DebouncedTextFilter } from '@components/general/DataTable/DebouncedTextFilter'
import {
  DragAlongBodyCell,
  DragAlongFilterCell,
  DraggableHeaderCell,
} from '@components/general/DataTable/DraggableCells'
import {
  readStoredState,
  writeStoredState,
} from '@components/general/DataTable/storedState'
import { useColumnPreferences } from '@components/general/DataTable/useColumnPreferences'
import { useColumnReorder } from '@components/general/DataTable/useColumnReorder'
import SectorIcon from '@components/general/SectorIcon/SectorIcon'
import {
  BATTERY_BAND_VALUES,
  BattIcon,
  getBatteryBands,
} from '@components/ui/BattIcon'
import {
  getSignalBand,
  SIGNAL_BAND_VALUES,
  SignalIcon,
} from '@components/ui/SignalIcon'
import { useAccounts } from '@data/accounts/accounts'
import Device, {
  DeviceIDTypes,
} from '@data/device/model/device'
import {
  DEVICE_STATE_LABELS,
  DEVICE_STATES,
  deviceStateLabel,
} from '@data/device/model/deviceState'
import { useDeviceDataUpdate } from '@data/device/device'
import { useVisibleLocations } from '@data/locations/locations'
import { useAuthStore } from '@store/auth.store'
import {
  COUNTRY_OPTIONS,
  PULSES_VALUES,
} from './constants'
import {
  buildGroupedDeviceRows,
  DeviceInformationRow,
  filterDeviceInformationRows,
  formatTableDate,
  getDeviceMeterType,
  getDisplayedStatus,
  hasActiveDeviceFilters,
  INSTALLED_VALVE_VALUES,
  orderGroupedRowsByRank,
  orderRowsByRank,
  rankDeviceInformationRows,
  readDateParam,
  readListParam,
  summarizeOnlineStatus,
  toSortableTime,
} from './deviceInformationTable.helpers'

const COLUMN_VISIBILITY_KEY = 'device-information-column-visibility-v1'
const COLUMN_SIZING_KEY = 'device-information-column-sizing-v1'
const FUZZY_SEARCH_KEY = 'device-information-fuzzy-search-v1'
const COLUMN_ORDER_KEY = 'device-information-column-order-v1'
const PAGE_SIZE = 30

const ADMIN_PERMISSION = 'UPDATE:DEVICE:PROD:ADMIN'
const DASHBOARD_STATUS_PERMISSION = 'DASHBOARD:STATUS:VIEW'
const LAST_SEEN_PERMISSION = 'ACCOUNT:ADMIN:SMARTFLOW'
// Smartflow staff only for now. Kept separate from LAST_SEEN_PERMISSION, which shares the
// value today, so the two can be opened up independently.
const ONLINE_SUMMARY_PERMISSION = 'ACCOUNT:ADMIN:SMARTFLOW'

const SHARED_COLUMN_ORDER = [
  'deviceName',
  'clientMeterId',
  'meterType',
  'accountName',
  'locationName',
  'locationCountry',
  'installDate',
  'type',
  'batteryLevel',
  'signalStrength',
  'batteryStatus',
  'deviceStatus',
  'dlId',
  'dUuid',
  'installedValves',
  'hasValveSchedule',
] as const

const ADMIN_COLUMN_ORDER = [
  'companyAlias',
  'report30Day',
  'pulsesPerLiter',
  'firmware',
  'ICCID',
  'mvno',
  'networkName',
  'deviceState',
  'deviceVendorName',
  'lastPing',
  'monitoringEnabled',
  'aasEnabled',
  'usageLoggingEnabled',
  'vacant',
  'noFlowThresholdHours',
  'missedCheckinThreshold',
  'uploadFreqMins',
] as const

const DEFAULT_COLUMN_VISIBILITY: ColumnVisibilityState = {
  clientMeterId: false,
  meterType: false,
  type: false,
  dlId: false,
  dUuid: false,
  companyAlias: false,
  report30Day: false,
  ICCID: false,
  mvno: false,
  monitoringEnabled: false,
  aasEnabled: false,
  usageLoggingEnabled: false,
  vacant: false,
  noFlowThresholdHours: false,
  missedCheckinThreshold: false,
  uploadFreqMins: false,
}

const TEXT_FILTER_PARAMS: Record<string, string> = {
  deviceName: 'deviceName',
  clientMeterId: 'clientMeterId',
  accountName: 'accountName',
  locationName: 'locationName',
  dlId: 'dlId',
  dUuid: 'dUuid',
  companyAlias: 'companyAlias',
  firmware: 'firmware',
  ICCID: 'ICCID',
  networkName: 'networkName',
}

type FilterOption = {
  value: string
  label: string
}

// Filters that accept several values. Each is stored in the URL as comma-separated values
// and matches a row holding any of them.
const MULTI_SELECT_FILTERS: Record<string, {
  param: string
  options: FilterOption[]
  // What the input shows when more than one value is picked (labels in option order).
  summarize: (labels: string[]) => string
  dropdownWidth?: number
}> = {
  deviceState: {
    param: 'deviceSettings.deviceState',
    options: DEVICE_STATES.map((state) => ({
      value: state,
      label: DEVICE_STATE_LABELS[state],
    })),
    summarize: (labels) => `${labels.length} states`,
    dropdownWidth: 220,
  },
  installedValves: {
    param: 'installedValves',
    options: INSTALLED_VALVE_VALUES.map((count) => ({
      value: count,
      label: count,
    })),
    summarize: (labels) => labels.join(', '),
  },
}

// Holds the selection locally rather than reading it straight from the URL. The URL write
// re-renders the table later than the next click can land, so a second pick made before
// then was computed from the stale value and overwrote the first. The URL stays the source
// of truth: any change to it (back/forward, clear, a shared link) re-syncs the selection.
const MultiSelectFilter = ({
  columnId,
  options,
  summarize,
  dropdownWidth,
  value,
  onChange,
}: {
  columnId: string
  options: FilterOption[]
  summarize: (labels: string[]) => string
  dropdownWidth?: number
  value: string | null
  onChange: (value: string | null) => void
}) => {
  const allowed = useMemo(() => options.map((option) => option.value), [options])
  const [selected, setSelected] = useState<string[]>(() => readListParam(value, allowed))

  useEffect(() => {
    setSelected(readListParam(value, allowed))
  }, [allowed, value])

  // Pills do not fit a filter cell, so they are hidden and the selection is summarised in
  // the placeholder, shown in the normal text colour once something is picked.
  const selectedLabels = options
    .filter((option) => selected.includes(option.value))
    .map((option) => option.label)
  const summary = selectedLabels.length === 0
    ? 'All'
    : selectedLabels.length === 1
      ? selectedLabels[0]
      : summarize(selectedLabels)

  return (
    <MultiSelect
      aria-label={`Filter ${columnId}`}
      placeholder={summary}
      size='xs'
      clearable
      data={options}
      value={selected}
      onChange={(values) => {
        setSelected(values)
        onChange(values.join(',') || null)
      }}
      comboboxProps={dropdownWidth ? { width: dropdownWidth } : undefined}
      styles={{
        pill: { display: 'none' },
        input: selected.length > 0
          ? { '--input-placeholder-color': 'var(--mantine-color-text)' } as CSSProperties
          : undefined,
      }}
    />
  )
}

const NUMERIC_FILTER_PARAMS: Record<string, string> = {
  noFlowThresholdHours: 'deviceSettings.noFlowThresholdHours',
  missedCheckinThreshold: 'deviceSettings.missedCheckinThreshold',
  uploadFreqMins: 'deviceSettings.uploadFreqMins',
}

const SELECT_FILTERS: Record<string, { param: string; options: string[] }> = {
  meterType: {
    param: 'meterType',
    options: ['Main meter', 'Submeter'],
  },
  locationCountry: {
    param: 'locationCountry',
    options: COUNTRY_OPTIONS,
  },
  type: {
    param: 'deviceSettings.type',
    options: ['Hot', 'Cold'],
  },
  batteryStatus: {
    param: 'batteryStatus',
    options: [...POWER_STATUS_VALUES],
  },
  batteryLevel: {
    param: 'batteryLevel',
    options: [...BATTERY_BAND_VALUES],
  },
  signalStrength: {
    param: 'signalStrength',
    options: [...SIGNAL_BAND_VALUES],
  },
  deviceStatus: {
    param: 'deviceStatus',
    options: ['Online', 'Offline'],
  },
  hasValveSchedule: {
    param: 'deviceSettings.hasValveSchedule',
    options: ['Yes', 'No'],
  },
  report30Day: {
    param: 'deviceSettings.report30Day',
    options: ['Yes', 'No'],
  },
  monitoringEnabled: {
    param: 'deviceSettings.monitoringEnabled',
    options: ['Yes', 'No'],
  },
  aasEnabled: {
    param: 'deviceSettings.aasEnabled',
    options: ['Yes', 'No'],
  },
  usageLoggingEnabled: {
    param: 'deviceSettings.usageLoggingEnabled',
    options: ['Yes', 'No'],
  },
  vacant: {
    param: 'deviceSettings.vacant',
    options: ['Yes', 'No'],
  },
  pulsesPerLiter: {
    param: 'deviceSettings.pulsesPerLiter',
    options: PULSES_VALUES,
  },
  mvno: {
    param: 'mvno',
    options: ['-', 'Emnify', 'EsEye', 'Onomondo', 'Simpl'],
  },
  deviceVendorName: {
    param: 'deviceVendorName',
    options: ['SF.A', 'SF.B', 'SF.H', 'SF.M', 'SF.P'],
  },
}

interface DeviceInformationTableProps {
  devices: Device[]
  onDeviceClick: (device: Device) => void
  onRefresh: () => void
  refreshing?: boolean
}

const features = tableFeatures({
  columnOrderingFeature,
  columnSizingFeature,
  columnResizingFeature,
  columnVisibilityFeature,
  rowSortingFeature,
  rowExpandingFeature,
  rowPaginationFeature,
  expandedRowModel: createExpandedRowModel(),
  sortedRowModel: createSortedRowModel(),
  paginatedRowModel: createPaginatedRowModel(),
  // Columns without an explicit sortFn resolve via 'auto', which only finds
  // functions registered here. An unregistered name silently degrades.
  sortFns: {
    alphanumeric: sortFn_alphanumeric,
    basic: sortFn_basic,
    datetime: sortFn_datetime,
    text: sortFn_text,
  },
})

const columnHelper = createColumnHelper<typeof features, DeviceInformationRow>()

const getCsvValue = (columnId: string, row: DeviceInformationRow) => {
  const { device } = row

  switch (columnId) {
    case 'deviceName':
      return `${device.deviceName} (${device.deviceId})`
    case 'clientMeterId':
      return device.deviceSettings.clientMeterId
    case 'meterType':
      return getDeviceMeterType(device)
    case 'accountName':
      return row.accountName
    case 'locationName':
      return row.locationName
    case 'locationCountry':
      return row.locationCountry
    case 'installDate':
      return formatTableDate(device.deviceSettings.installDate)
    case 'type':
      return device.deviceSettings.type
    case 'batteryLevel':
      return device.batteryLevel ?? ''
    case 'signalStrength':
      return device.signal ?? ''
    case 'batteryStatus':
      return row.powerStatus
    case 'deviceStatus':
      return row.deviceStatus
    case 'dlId':
      return device.dlId
    case 'dUuid':
      return device.dUuid
    case 'installedValves':
      return row.installedValves
    case 'hasValveSchedule':
      return device.deviceSettings.hasValveSchedule ? 'Yes' : 'No'
    case 'companyAlias':
      return row.companyAlias
    case 'report30Day':
      return device.deviceSettings.report30Day ? 'Yes' : 'No'
    case 'pulsesPerLiter':
      return device.deviceSettings.pulsesPerLiter
    case 'firmware':
      return device.firmware
    case 'ICCID':
      return device.ICCID ?? ''
    case 'mvno':
      return device.mvno ?? '-'
    case 'networkName':
      return device.networkName ?? ''
    case 'deviceState':
      return deviceStateLabel(device.deviceSettings.deviceState)
    case 'deviceVendorName':
      return device.deviceVendorName ?? ''
    case 'lastPing':
      return formatTableDate(row.lastPing, true)
    case 'monitoringEnabled':
      return device.deviceSettings.monitoringEnabled ? 'Yes' : 'No'
    case 'aasEnabled':
      return device.deviceSettings.aasEnabled ? 'Yes' : 'No'
    case 'usageLoggingEnabled':
      return device.deviceSettings.usageLoggingEnabled ? 'Yes' : 'No'
    case 'vacant':
      return device.deviceSettings.vacant ? 'Yes' : 'No'
    case 'noFlowThresholdHours':
      return device.deviceSettings.noFlowThresholdHours
    case 'missedCheckinThreshold':
      return device.deviceSettings.missedCheckinThreshold
    case 'uploadFreqMins':
      return device.deviceSettings.uploadFreqMins
    default:
      return ''
  }
}

export const DeviceInformationTable = ({
  devices,
  onDeviceClick,
  onRefresh,
  refreshing = false,
}: DeviceInformationTableProps) => {
  const permissions = useAuthStore((state) => state.permissions)
  const accounts = useAccounts()
  const locations = useVisibleLocations()
  const { mutateAsync: updateDeviceData } = useDeviceDataUpdate()
  const [searchParams, setSearchParams] = useSearchParams()

  const isAdmin = hasPermissions(permissions, [ADMIN_PERMISSION])
  const hasDashboardStatusView = hasPermissions(permissions, [DASHBOARD_STATUS_PERMISSION])
  const canSeeLastSeen = hasPermissions(permissions, [LAST_SEEN_PERMISSION])
  const canSeeOnlineSummary = hasPermissions(permissions, [ONLINE_SUMMARY_PERMISSION])
  const hasSubMeters = useMemo(
    () => devices.some((device) =>
      device.deviceSettings.masterDeviceIdRef !== undefined &&
      device.deviceSettings.masterDeviceIdRef !== null,
    ),
    [devices],
  )
  const grouped = hasSubMeters && searchParams.get('grouped') === 'true'
  const currentPage = Math.max(1, Number(searchParams.get('page')) || 1)

  // Held in both the URL and localStorage, which no other control here needs. An explicit
  // parameter wins so a shared link shows the recipient what the sender saw; with no parameter
  // there is nothing to reproduce and the user's own preference applies. That is why the
  // parameter is written as 'false' rather than removed — encoding off as absence would let a
  // recipient's stored preference silently widen a link that was shared with fuzzy off.
  const [storedFuzzy, setStoredFuzzy] = useState(
    () => readStoredState<unknown>(FUZZY_SEARCH_KEY, false) === true,
  )
  const fuzzyParam = searchParams.get('fuzzy')
  const fuzzy = fuzzyParam === null ? storedFuzzy : fuzzyParam === 'true'
  const canonicalColumnOrder = useMemo(
    () => [
      ...SHARED_COLUMN_ORDER,
      ...(isAdmin ? ADMIN_COLUMN_ORDER : []),
    ],
    [isAdmin],
  )

  const [sorting, setSorting] = useState<SortingState>([])
  const [expanded, setExpanded] = useState<ExpandedState>(grouped ? true : {})
  const {
    columnOrder,
    columnSizing,
    columnVisibility,
    handleColumnOrderChange,
    handleColumnSizingChange,
    handleColumnVisibilityChange,
    reportStorageUnavailable,
    storageAvailable,
  } = useColumnPreferences({
    storageKeys: {
      visibility: COLUMN_VISIBILITY_KEY,
      sizing: COLUMN_SIZING_KEY,
      order: COLUMN_ORDER_KEY,
    },
    canonicalColumnOrder,
    defaultColumnVisibility: DEFAULT_COLUMN_VISIBILITY,
    storageProbeKey: 'device-information-storage-probe',
    storageFailureMessage: 'Table preferences can\'t be saved in this browser — your column selection, widths, order, and search mode will reset when you reload.',
  })
  const [mobileControlsVisible, setMobileControlsVisible] = useState(true)

  const accountNames = useMemo(
    () => new Map(accounts.map((account) => [account.id, account.accountName])),
    [accounts],
  )
  const locationsById = useMemo(
    () => new Map(locations.map((location) => [location.id, location])),
    [locations],
  )

  const flatRows = useMemo<DeviceInformationRow[]>(
    () => devices.map((device) => {
      const status = getDeviceStatus(device, hasDashboardStatusView)
      const location = locationsById.get(device.deviceLocationId)

      return {
        device,
        accountName: accountNames.get(device.accountId) ?? '-',
        locationName: location?.name ?? '-',
        locationCountry: location?.country ?? '-',
        companyAlias: location?.companyAlias ?? '-',
        installedValves: countInstalledValves(device),
        // The `batteryStatus` gate lives here rather than in the cell so that the filter and
        // the rendered value are the same string by construction. A device with no
        // `batteryStatus` shows a ban icon and matches no Power selection — previously the cell
        // gated on it while the filter did not, so filtering could return rows displaying a ban.
        powerStatus: device.batteryStatus ? getPowerStatus(device) : '',
        // Same bands the icons render, so each filter matches what its cell shows. A device with
        // no reading gets no band, displays a ban icon or N/A, and matches no selection.
        batteryBands: getBatteryBands(device),
        signalBand: getSignalBand(device.signal),
        deviceStatus: typeof status === 'string' ? status : '',
        installDate: toSortableTime(device.deviceSettings.installDate),
        lastPing: toSortableTime(device.lastOnline),
      }
    }),
    [accountNames, devices, hasDashboardStatusView, locationsById],
  )

  const filtersActive = hasActiveDeviceFilters(searchParams, isAdmin)
  const matchingRows = useMemo(
    () => filterDeviceInformationRows(flatRows, searchParams, isAdmin, fuzzy),
    [flatRows, fuzzy, isAdmin, searchParams],
  )
  const matchingIds = useMemo(
    () => new Set(matchingRows.map((row) => row.device.dUuid)),
    [matchingRows],
  )
  const onlineSummary = useMemo(
    () => summarizeOnlineStatus(matchingRows),
    [matchingRows],
  )
  const groupedRows = useMemo(
    () => buildGroupedDeviceRows(flatRows, matchingIds, filtersActive),
    [filtersActive, flatRows, matchingIds],
  )

  // Relevance has no column to sort by, so it is applied to the data before the table sees it:
  // that makes rank the natural order, and any column sort the user picks overrides it through
  // getSortedRowModel. Only worth computing when it will actually be used.
  const rankOrderActive = fuzzy &&
    sorting.length === 0 &&
    Boolean(searchParams.get('deviceName') || searchParams.get('accountName'))
  const searchRanks = useMemo(
    () => (rankOrderActive
      ? rankDeviceInformationRows(matchingRows, searchParams, fuzzy)
      : null),
    [fuzzy, matchingRows, rankOrderActive, searchParams],
  )
  const orderedRows = useMemo(
    () => (searchRanks ? orderRowsByRank(matchingRows, searchRanks) : matchingRows),
    [matchingRows, searchRanks],
  )
  const orderedGroupedRows = useMemo(
    () => (searchRanks ? orderGroupedRowsByRank(groupedRows, searchRanks) : groupedRows),
    [groupedRows, searchRanks],
  )
  const tableData = grouped ? orderedGroupedRows : orderedRows

  const updateSearchParams = useCallback((updates: Record<string, string | null>) => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous)

      Object.entries(updates).forEach(([key, value]) => {
        if (value) next.set(key, value)
        else next.delete(key)
      })

      next.set('page', '1')
      return next
    })
  }, [setSearchParams])

  // Writes both halves. The parameter is always written, including 'false', so that absence
  // keeps meaning "no opinion, use the stored preference" — see the note on `fuzzy` above.
  const handleFuzzyChange = useCallback((checked: boolean) => {
    setStoredFuzzy(checked)
    if (!writeStoredState(FUZZY_SEARCH_KEY, checked)) reportStorageUnavailable()
    updateSearchParams({ fuzzy: String(checked) })
  }, [reportStorageUnavailable, updateSearchParams])

  const updateReport30Day = useCallback((device: Device, checked: boolean) => {
    const updatedDevice = {
      ...device,
      deviceSettings: {
        ...device.deviceSettings,
        report30Day: checked,
      },
    }

    void updateDeviceData({
      deviceId: device.dUuid,
      idType: DeviceIDTypes.dUuid,
      device: updatedDevice,
    })
  }, [updateDeviceData])

  const columns = useMemo(() => {
    const sharedColumns = columnHelper.columns([
      columnHelper.accessor((row) => row.device.deviceName, {
        id: 'deviceName',
        header: 'Device Name/ID',
        enableHiding: false,
        size: 260,
        cell: ({ row }) => {
          const { device } = row.original
          const subMeterCount = grouped
            ? row.subRows.length
            : device.childDeviceIds?.length ?? 0

          return (
            <Group
              data-testid={`device-name-group-${device.dUuid}`}
              gap='xs'
              wrap='nowrap'
              pl={grouped && row.depth > 0 ? 52 : 0}
            >
              {row.getCanExpand() ? (
                <ActionIcon
                  aria-label={row.getIsExpanded() ? 'Collapse sub-meters' : 'Expand sub-meters'}
                  variant='subtle'
                  size='sm'
                  onClick={(event) => {
                    event.stopPropagation()
                    row.getToggleExpandedHandler()()
                  }}
                >
                  {row.getIsExpanded() ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </ActionIcon>
              ) : null}

              <Box data-testid={`device-name-content-${device.dUuid}`}>
                <SectorIcon
                  sector={device.deviceSettings.sectorType}
                  occupants={device.deviceSettings.occupants}
                />{' '}
                {device.deviceName}
                <br />
                {device.deviceId}
                {subMeterCount > 0 ? (
                  <>
                    <br />
                    <Badge
                      color='blue'
                      variant='filled'
                      size='xs'
                    >
                      MAIN METER
                      {grouped
                        ? ` · ${subMeterCount} SUB-METER${subMeterCount === 1 ? '' : 'S'}`
                        : ''}
                    </Badge>
                  </>
                ) : null}
                {device.deviceSettings.masterDeviceIdRef !== undefined &&
                  device.deviceSettings.masterDeviceIdRef !== null ? (
                  <>
                    <br />
                    <Badge
                      color='blue.4'
                      variant='filled'
                      size='xs'
                    >
                      SUB METER
                    </Badge>
                  </>
                ) : null}
              </Box>
            </Group>
          )
        },
      }),
      columnHelper.accessor((row) => row.device.deviceSettings.clientMeterId, {
        id: 'clientMeterId',
        header: 'Client Meter ID',
        size: 180,
        sortUndefined: 'last',
      }),
      columnHelper.accessor((row) => getDeviceMeterType(row.device), {
        id: 'meterType',
        header: 'Meter Type',
        size: 150,
      }),
      columnHelper.accessor('accountName', {
        header: 'Account Name',
        enableSorting: false,
        size: 250,
      }),
      columnHelper.accessor('locationName', {
        header: 'Location Name',
        enableSorting: false,
        size: 250,
      }),
      columnHelper.accessor('locationCountry', {
        header: 'Location Country',
        enableSorting: false,
        size: 220,
      }),
      columnHelper.accessor('installDate', {
        id: 'installDate',
        header: 'Install Date',
        size: 190,
        sortUndefined: 'last',
        cell: ({ row }) => formatTableDate(row.original.device.deviceSettings.installDate),
      }),
      columnHelper.accessor((row) => row.device.deviceSettings.type, {
        id: 'type',
        header: 'Type',
        size: 120,
      }),
      columnHelper.accessor((row) => row.device.batteryLevel, {
        id: 'batteryLevel',
        header: 'Battery',
        size: 180,
        sortUndefined: 'last',
        cell: ({ row }) => <BattIcon device={row.original.device} />,
      }),
      columnHelper.accessor((row) => row.device.signal, {
        id: 'signalStrength',
        header: 'Signal',
        size: 150,
        sortUndefined: 'last',
        cell: ({ row }) => <SignalIcon signalLevel={row.original.device.signal} />,
      }),
      columnHelper.accessor('powerStatus', {
        id: 'batteryStatus',
        header: 'Power',
        size: 160,
        cell: ({ row }) => row.original.powerStatus || <FaBan />,
      }),
      columnHelper.accessor('deviceStatus', {
        header: 'Status',
        size: 180,
        cell: ({ row }) => {
          const { device } = row.original
          const deviceStatus = getDisplayedStatus(row.original)

          if (!deviceStatus) return <FaBan />
          if (deviceStatus === 'Offline' && canSeeLastSeen) {
            return (
              <>
                Offline
                <Text span fz='0.7em'>
                  {' '}(last seen: {moment(device.lastOnline).fromNow()})
                </Text>
              </>
            )
          }

          return deviceStatus
        },
      }),
      columnHelper.accessor((row) => row.device.dlId, {
        id: 'dlId',
        header: 'DLID',
        enableSorting: false,
        size: 180,
      }),
      columnHelper.accessor((row) => row.device.dUuid, {
        id: 'dUuid',
        header: 'UUID',
        enableSorting: false,
        size: 250,
      }),
      columnHelper.accessor('installedValves', {
        header: 'Valves',
        size: 120,
        cell: ({ getValue }) => getValue() === 0
          ? <Text c='dimmed'>-</Text>
          : <span className='badge bg-info'>{getValue()}</span>,
      }),
      columnHelper.accessor((row) => row.device.deviceSettings.hasValveSchedule, {
        id: 'hasValveSchedule',
        header: 'Schedule',
        size: 130,
        cell: ({ getValue }) => getValue()
          ? <FaCheckCircle color='#198754' title='Schedule present' />
          : <Text c='dimmed'>-</Text>,
      }),
    ])

    if (!isAdmin) return sharedColumns

    return columnHelper.columns([
      ...sharedColumns,
      columnHelper.accessor('companyAlias', {
        header: 'Sub Entity',
        enableSorting: false,
        size: 220,
      }),
      columnHelper.accessor((row) => row.device.deviceSettings.report30Day, {
        id: 'report30Day',
        header: '30 Day Report',
        size: 170,
        cell: ({ row, getValue }) => (
          <Checkbox
            aria-label={`Toggle 30 Day Report for ${row.original.device.deviceName}`}
            checked={getValue()}
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => updateReport30Day(
              row.original.device,
              event.currentTarget.checked,
            )}
          />
        ),
      }),
      columnHelper.accessor((row) => row.device.deviceSettings.pulsesPerLiter, {
        id: 'pulsesPerLiter',
        header: 'Pulses',
        size: 130,
      }),
      columnHelper.accessor((row) => row.device.firmware, {
        id: 'firmware',
        header: 'Firmware',
        size: 180,
      }),
      columnHelper.accessor((row) => row.device.ICCID, {
        id: 'ICCID',
        header: 'ICCID',
        size: 220,
        sortUndefined: 'last',
      }),
      columnHelper.accessor((row) => row.device.mvno || '-', {
        id: 'mvno',
        header: 'MVNO',
        size: 150,
      }),
      columnHelper.accessor((row) => row.device.networkName, {
        id: 'networkName',
        header: 'Network',
        size: 160,
        sortUndefined: 'last',
      }),
      columnHelper.accessor((row) => deviceStateLabel(row.device.deviceSettings.deviceState), {
        id: 'deviceState',
        header: 'State',
        size: 180,
      }),
      columnHelper.accessor((row) => row.device.deviceVendorName, {
        id: 'deviceVendorName',
        header: 'Vendor',
        size: 150,
        sortUndefined: 'last',
      }),
      columnHelper.accessor('lastPing', {
        header: 'Last Check In',
        size: 190,
        sortUndefined: 'last',
        cell: ({ getValue }) => formatTableDate(getValue(), true),
      }),
      columnHelper.accessor(
        (row) => row.device.deviceSettings.monitoringEnabled ? 'Yes' : 'No',
        {
          id: 'monitoringEnabled',
          header: 'Monitoring Enabled',
          size: 190,
        },
      ),
      columnHelper.accessor(
        (row) => row.device.deviceSettings.aasEnabled ? 'Yes' : 'No',
        {
          id: 'aasEnabled',
          header: 'AAS Enabled',
          size: 150,
        },
      ),
      columnHelper.accessor(
        (row) => row.device.deviceSettings.usageLoggingEnabled ? 'Yes' : 'No',
        {
          id: 'usageLoggingEnabled',
          header: 'Usage Logging Enabled',
          size: 210,
        },
      ),
      columnHelper.accessor(
        (row) => row.device.deviceSettings.vacant ? 'Yes' : 'No',
        {
          id: 'vacant',
          header: 'Vacant',
          size: 120,
        },
      ),
      columnHelper.accessor((row) => row.device.deviceSettings.noFlowThresholdHours, {
        id: 'noFlowThresholdHours',
        header: 'No Flow Threshold (Hours)',
        size: 220,
        sortUndefined: 'last',
      }),
      columnHelper.accessor((row) => row.device.deviceSettings.missedCheckinThreshold, {
        id: 'missedCheckinThreshold',
        header: 'Max Missed Check-ins',
        size: 200,
        sortUndefined: 'last',
      }),
      columnHelper.accessor((row) => row.device.deviceSettings.uploadFreqMins, {
        id: 'uploadFreqMins',
        header: 'Upload Frequency (Mins)',
        size: 220,
        sortUndefined: 'last',
      }),
    ])
  }, [canSeeLastSeen, grouped, isAdmin, updateReport30Day])

  useEffect(() => {
    setExpanded(grouped ? true : {})
  }, [grouped])

  useEffect(() => {
    if (!grouped || !filtersActive) return

    setExpanded((previous) => {
      if (previous === true) return previous

      const next = { ...previous }
      groupedRows.forEach((row) => {
        if (row.subRows && row.subRows.length > 0) next[row.device.dUuid] = true
      })
      return next
    })
  }, [filtersActive, grouped, groupedRows])

  const table = useTable({
    features,
    data: tableData,
    columns,
    state: {
      sorting,
      columnVisibility,
      columnSizing,
      columnOrder,
      expanded,
      pagination: {
        pageIndex: currentPage - 1,
        pageSize: PAGE_SIZE,
      },
    },
    onSortingChange: setSorting,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    onColumnSizingChange: handleColumnSizingChange,
    onColumnOrderChange: handleColumnOrderChange,
    onExpandedChange: setExpanded,
    onPaginationChange: (updater) => {
      const current = {
        pageIndex: currentPage - 1,
        pageSize: PAGE_SIZE,
      }
      const next = typeof updater === 'function' ? updater(current) : updater

      setSearchParams((previous) => {
        const params = new URLSearchParams(previous)
        params.set('page', String(next.pageIndex + 1))
        return params
      })
    },
    getRowId: (row) => row.device.dUuid || row.device.deviceId,
    getSubRows: (row) => row.subRows,
    paginateExpandedRows: false,
    autoResetPageIndex: false,
    // Expansion is derived from `grouped` and the active filters by the effects
    // above. v9 resets expanded state on every `data` identity change, which
    // would clobber that the moment devices load.
    autoResetExpanded: false,
    columnResizeMode: 'onChange',
  })

  const pageCount = table.getPageCount()

  useEffect(() => {
    const lastPage = Math.max(pageCount, 1)
    if (currentPage <= lastPage) return

    setSearchParams((previous) => {
      const next = new URLSearchParams(previous)
      next.set('page', String(lastPage))
      return next
    })
  }, [currentPage, pageCount, setSearchParams])

  const visibleColumns = table.getVisibleLeafColumns()
  const visibleColumnIds = visibleColumns.map((column) => column.id)
  const {
    handleColumnDragEnd,
    handleKeyboardColumnMove,
    sensors,
  } = useColumnReorder(handleColumnOrderChange, visibleColumnIds)
  const orderedHideableColumns = columnOrder.flatMap((id) => {
    const column = table.getColumn(id)
    return column?.getCanHide() ? [column] : []
  })
  const exportedDeviceIds = new Set<string>()
  const exportRows = table.getPrePaginatedRowModel().flatRows.reduce<DeviceInformationRow[]>(
    (result, row) => {
      const deviceId = row.original.device.dUuid
      if (matchingIds.has(deviceId) && !exportedDeviceIds.has(deviceId)) {
        result.push(row.original)
        exportedDeviceIds.add(deviceId)
      }

      return result
    },
    [],
  )
  const csvHeaders = visibleColumns.map((column) =>
    typeof column.columnDef.header === 'string' ? column.columnDef.header : column.id,
  )
  const csvData = [
    grouped ? [...csvHeaders, 'Master DLID'] : csvHeaders,
    ...exportRows.map((row) => {
      const values = visibleColumns.map((column) => getCsvValue(column.id, row))
      if (!grouped) return values

      return [
        ...values,
        row.device.deviceSettings.masterDeviceIdRef ?? '',
      ]
    }),
  ]

  const renderFilter = (columnId: string) => {
    const textParam = TEXT_FILTER_PARAMS[columnId]
    if (textParam) {
      return (
        <DebouncedTextFilter
          ariaLabel={`Filter ${columnId}`}
          value={searchParams.get(textParam) ?? ''}
          onChange={(value) => updateSearchParams({ [textParam]: value || null })}
        />
      )
    }

    const multiSelectFilter = MULTI_SELECT_FILTERS[columnId]
    if (multiSelectFilter) {
      return (
        <MultiSelectFilter
          columnId={columnId}
          options={multiSelectFilter.options}
          summarize={multiSelectFilter.summarize}
          dropdownWidth={multiSelectFilter.dropdownWidth}
          value={searchParams.get(multiSelectFilter.param)}
          onChange={(value) => updateSearchParams({ [multiSelectFilter.param]: value })}
        />
      )
    }

    const selectFilter = SELECT_FILTERS[columnId]
    if (selectFilter) {
      return (
        <Select
          aria-label={`Filter ${columnId}`}
          placeholder='All'
          size='xs'
          searchable={columnId === 'locationCountry'}
          clearable
          data={selectFilter.options}
          value={searchParams.get(selectFilter.param)}
          onChange={(value) => updateSearchParams({ [selectFilter.param]: value })}
        />
      )
    }

    const numericParam = NUMERIC_FILTER_PARAMS[columnId]
    if (numericParam) {
      return (
        <DebouncedTextFilter
          ariaLabel={`Filter ${columnId}`}
          numeric
          value={searchParams.get(numericParam) ?? ''}
          onChange={(value) => updateSearchParams({ [numericParam]: value || null })}
        />
      )
    }

    if (columnId === 'installDate' || columnId === 'lastPing') {
      const fromParam = columnId === 'installDate' ? 'fromDate' : 'lastPingFrom'
      const toParam = columnId === 'installDate' ? 'toDate' : 'lastPingTo'

      return (
        <DatePickerInput
          aria-label={`Filter ${columnId} by date range`}
          type='range'
          placeholder='Select range...'
          size='xs'
          value={[
            readDateParam(searchParams.get(fromParam)),
            readDateParam(searchParams.get(toParam)),
          ]}
          onChange={(value) => updateSearchParams({
            [fromParam]: value[0],
            [toParam]: value[1],
          })}
          leftSection={<Calendar size={12} />}
          clearable
        />
      )
    }

    return null
  }

  const paginatedRows = table.getRowModel().rows
  const rootRowCount = tableData.length
  const firstVisibleRoot = rootRowCount === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1
  const lastVisibleRoot = Math.min(currentPage * PAGE_SIZE, rootRowCount)

  return (
    <Box
      data-testid='device-information-table'
      style={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        minHeight: 0,
        minWidth: 0,
      }}
    >
      <Group
        data-testid='device-table-toolbar'
        align='center'
        gap='sm'
        wrap='wrap'
        mb={{ base: mobileControlsVisible ? 'md' : 'xs', sm: 'md' }}
        mt={{ base: mobileControlsVisible ? 'md' : 'xs', sm: 'md' }}
      >
        <Tooltip
          label={mobileControlsVisible ? 'Hide table controls' : 'Show table controls'}
          withArrow
        >
          <ActionIcon
            aria-label={mobileControlsVisible ? 'Hide table controls' : 'Show table controls'}
            hiddenFrom='sm'
            variant='subtle'
            size='sm'
            onClick={() => setMobileControlsVisible((visible) => !visible)}
          >
            {mobileControlsVisible ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </ActionIcon>
        </Tooltip>

        <Tooltip label='Refresh devices' withArrow>
          <Button
            aria-label='Refresh Devices'
            size='sm'
            variant='outline'
            px={{ base: 'xs', sm: 'md' }}
            display={{ base: mobileControlsVisible ? 'inline-flex' : 'none', sm: 'inline-flex' }}
            onClick={onRefresh}
            loading={refreshing}
          >
            <Group gap='xs' wrap='nowrap'>
              <RefreshCw
                data-testid='refresh-devices-icon'
                aria-hidden
                size={16}
              />
              <Box
                component='span'
                data-testid='refresh-devices-label'
                visibleFrom='sm'
              >
                Refresh
              </Box>
            </Group>
          </Button>
        </Tooltip>

        <Tooltip label='Export devices to CSV' withArrow>
          <Box
            component='span'
            display={{ base: mobileControlsVisible ? 'inline-block' : 'none', sm: 'inline-block' }}
          >
            <Button
              component={CSVLink}
              aria-label='Generate CSV Report'
              data={csvData}
              filename={`filteredDevices_${format(new Date(), 'dd-MM-yyyy_HH-mm')}.csv`}
              px={{ base: 'xs', sm: 'md' }}
            >
              <Group gap='xs' wrap='nowrap'>
                <Download
                  data-testid='export-devices-icon'
                  aria-hidden
                  size={16}
                />
                <Box
                  component='span'
                  data-testid='export-devices-label'
                  visibleFrom='sm'
                >
                  Export
                </Box>
              </Group>
            </Button>
          </Box>
        </Tooltip>

        {/*
          Auto margins on both this badge and the settings button split the free space either
          side of the badge, centring it in the toolbar gap while settings stays at the right.
        */}
        {canSeeOnlineSummary && onlineSummary.total > 0 ? (
          <Tooltip
            label={`${onlineSummary.online} of ${onlineSummary.total} devices online. Only devices showing a status are counted.`}
            multiline
            w={260}
            withArrow
          >
            <Badge
              data-testid='device-online-summary'
              ml='auto'
              size='lg'
              variant='light'
              color={onlineSummary.online / onlineSummary.total > 0.9 ? 'green' : 'red'}
            >
              {formatPercentage(onlineSummary.online / onlineSummary.total)} online
            </Badge>
          </Tooltip>
        ) : null}

        <Popover
          position='bottom-start'
          withArrow
          shadow='md'
        >
          <Popover.Target>
            <Box
              component='span'
              ml='auto'
              display={{ base: mobileControlsVisible ? 'inline-block' : 'none', sm: 'inline-block' }}
            >
              <Tooltip label='Table settings' withArrow>
                <Button
                  aria-label='Table settings'
                  size='sm'
                  variant='light'
                  px={{ base: 'xs', sm: 'md' }}
                >
                  <Group gap='xs' wrap='nowrap'>
                    <SlidersHorizontal
                      data-testid='settings-icon'
                      aria-hidden
                      size={14}
                    />
                    <Box
                      component='span'
                      data-testid='settings-label'
                      visibleFrom='sm'
                    >
                      Settings
                    </Box>
                  </Group>
                </Button>
              </Tooltip>
            </Box>
          </Popover.Target>
          <Popover.Dropdown>
            <Stack gap='xs' miw={220}>
              <Text
                size='xs'
                fw={600}
                c='dimmed'
                tt='uppercase'
              >
                Toggle columns
              </Text>
              <ScrollArea.Autosize
                data-testid='device-column-checkbox-scroll'
                mah='50vh'
                type='auto'
                scrollbarSize={8}
                viewportProps={{ id: 'device-column-checkbox-scroll-viewport' }}
              >
                <Stack gap='xs' pr='xs'>
                  {orderedHideableColumns.map((column) => (
                    <Checkbox
                      key={column.id}
                      label={typeof column.columnDef.header === 'string'
                        ? column.columnDef.header
                        : column.id}
                      checked={column.getIsVisible()}
                      onChange={column.getToggleVisibilityHandler()}
                      size='sm'
                    />
                  ))}
                </Stack>
              </ScrollArea.Autosize>
              {!storageAvailable ? (
                <Text size='xs' c='yellow.8'>
                  Column selection, widths, and order will reset when this page reloads.
                </Text>
              ) : null}

              {/*
                The behaviour switches live in here rather than on the toolbar so they keep full
                labels at every width. On the toolbar they had to compete with the icon-only
                actions for a 432px row, and dropping their labels to fit left an anonymous
                toggle sitting beside a labelled one. A popover has room for both to say what
                they do, and neither is gated by breakpoint, so a mode enabled on a wide layout
                can always be turned off on a narrow one — the failure F5 records.
              */}
              <Divider />

              <Text
                size='xs'
                fw={600}
                c='dimmed'
                tt='uppercase'
              >
                Behaviour
              </Text>

              {hasSubMeters ? (
                <Switch
                  label='Group sub-meters'
                  description='Nest sub-meters under the meter they belong to'
                  checked={grouped}
                  onChange={(event) => updateSearchParams({
                    grouped: event.currentTarget.checked ? 'true' : null,
                  })}
                />
              ) : null}

              <Switch
                label='Fuzzy search'
                description='Match device and account names loosely'
                checked={fuzzy}
                onChange={(event) => handleFuzzyChange(event.currentTarget.checked)}
              />
            </Stack>
          </Popover.Dropdown>
        </Popover>
      </Group>

      <Paper
        data-testid='device-table-card'
        shadow='sm'
        radius='md'
        withBorder
        style={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          minHeight: 0,
          minWidth: 0,
        }}
      >
        <Table.ScrollContainer
          minWidth={1200}
          type='native'
          style={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
          }}
        >
          <DndContext
            collisionDetection={closestCenter}
            modifiers={[restrictToHorizontalAxis]}
            sensors={sensors}
            onDragEnd={handleColumnDragEnd}
          >
            <Table
              striped
              highlightOnHover
              stickyHeader
              layout='fixed'
              horizontalSpacing={10}
              verticalSpacing={8}
              styles={{
                thead: {
                  boxShadow: '0 2px 4px rgb(0 0 0 / 10%)',
                },
                th: {
                  fontSize: 12,
                },
              }}
              style={{
                width: table.getTotalSize(),
              }}
            >
              <Table.Thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <Fragment key={headerGroup.id}>
                    <Table.Tr>
                      <SortableContext
                        items={visibleColumnIds}
                        strategy={horizontalListSortingStrategy}
                      >
                        {headerGroup.headers.map((header) => (
                          <DraggableHeaderCell
                            key={header.id}
                            header={header}
                            onKeyboardMove={handleKeyboardColumnMove}
                          />
                        ))}
                      </SortableContext>
                    </Table.Tr>

                    <Table.Tr>
                      <SortableContext
                        items={visibleColumnIds}
                        strategy={horizontalListSortingStrategy}
                      >
                        {headerGroup.headers.map((header) => (
                          <DragAlongFilterCell
                            key={`${header.id}-filter`}
                            header={header}
                          >
                            {renderFilter(header.column.id)}
                          </DragAlongFilterCell>
                        ))}
                      </SortableContext>
                    </Table.Tr>
                  </Fragment>
                ))}
              </Table.Thead>

              <Table.Tbody>
                {paginatedRows.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={visibleColumns.length}>
                      <Text
                        ta='center'
                        c='dimmed'
                        py='xl'
                      >
                        No devices found
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : paginatedRows.map((row) => (
                  <Table.Tr
                    key={row.id}
                    bg={row.original.contextOnly ? 'blue.0' : undefined}
                    style={{ cursor: 'pointer' }}
                    onClick={() => onDeviceClick(row.original.device)}
                  >
                    <SortableContext
                      items={visibleColumnIds}
                      strategy={horizontalListSortingStrategy}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <DragAlongBodyCell
                          key={cell.id}
                          cell={cell}
                        />
                      ))}
                    </SortableContext>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </DndContext>
        </Table.ScrollContainer>

        {rootRowCount > 0 ? (
          <>
            <Divider />
            <Group
              justify='space-between'
              p='md'
            >
              <Text
                size='md'
                fw={600}
                c='var(--mantine-color-text)'
              >
                {grouped
                  ? `Showing groups ${firstVisibleRoot}–${lastVisibleRoot} of ${rootRowCount} (${matchingRows.length} matching devices)`
                  : `Showing ${firstVisibleRoot}–${lastVisibleRoot} of ${matchingRows.length} devices`}
              </Text>

              {pageCount > 1 ? (
                <Pagination
                  size='sm'
                  total={pageCount}
                  value={currentPage}
                  onChange={(page) => table.setPageIndex(page - 1)}
                />
              ) : null}
            </Group>
          </>
        ) : null}
      </Paper>
    </Box>
  )
}
